import FileTextIcon from '@lift/assets/dist/assets/globals/icons/file_text';
import { LftButton, LftTypography } from '@lift/design-system-react-web/dist/components';
import { useStore } from 'effector-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { ConfigsManager } from '../../infra/configs/configs.manager';
import { t } from '../../locales/i18n';
import { $isLead, $usuario } from '../../stores/usuario/usuario.store';
import { converterParaKebabCase } from '../../utils/formatters/converter-string/converter-string';
import { setBodyProps } from '../../utils/set-body-props/set-body-props';
import DadosUsuario from '../dados-usuario/dados-usuario.component';
import { Logout } from '../logout/logout.component';
import { MenuAcessibilidade } from '../menu-acessibilidade';
import { AvatarUsuario } from '../menu-avatar/fragments/avatar-usuario/avatar-usuario.fragment';
import { ConteudoMenuMobileAvatarFragment } from './fragments/conteudo-menu-mobile-avatar/conteudo-menu-mobile-avatar.fragment';
import S from './menu-mobile-avatar.styles';

const MenuMobileAvatar: React.FC = () => {
  const { pathname } = useLocation();
  const { isEnabled } = ConfigsManager.getInstance();
  const usuario = useStore($usuario);
  const isLead = useStore($isLead);

  const [open, setOpen] = useState(false);
  const [rotaAtual, setRotaAtual] = useState<string | undefined>(pathname);

  const handleClickDocument = useCallback((documentType: 'linkPrivatePolicy' | 'linkGrupoEconomico') => {
    const url = t(`modalAvatar.termosCondicoes.${documentType}`);

    window.open(url, '_blank');

    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          method: 'openExternalBrowser',
          url: url,
        }),
      );
    }
  }, []);

  const handleClickPrivatePolicy = useCallback(() => {
    handleClickDocument('linkPrivatePolicy');
  }, [handleClickDocument]);

  const handleClickEconomicGroup = useCallback(() => {
    handleClickDocument('linkGrupoEconomico');
  }, [handleClickDocument]);

  useEffect(() => {
    if (open) {
      setBodyProps({ overflow: 'hidden', ariaHidden: true });
      return;
    }

    setBodyProps({ overflow: 'visible', ariaHidden: false });
  }, [open]);

  const toggleMenu = useCallback(() => {
    setOpen(!open);
  }, [open]);

  useEffect(() => {
    if (rotaAtual !== pathname) setOpen(false);

    setRotaAtual(pathname);
  }, [pathname, rotaAtual]);

  return (
    <>
      <S.AvatarButton
        id="menu-mobile-avatar"
        aria-label="Abrir menu do usuário"
        onClick={toggleMenu}
        data-testid="abrir-menu-mobile-avatar"
      >
        <AvatarUsuario nomeUsuario={usuario?.nome} emailUsuario={usuario?.email} size="lg" />
      </S.AvatarButton>

      {open && (
        <ConteudoMenuMobileAvatarFragment
          conteudo={
            <S.MenuMobileAvatarContent>
              <DadosUsuario />

              {isEnabled('FF_EXIBE_BOTOES_TERMOS_AVATAR') && (
                <S.TermosCondicoesContent>
                  <S.TermosCondicoesTitulo>
                    <LftTypography component="paragraph" size="small" fontWeight="bold">
                      {t('modalAvatar.termosCondicoes.titulo')}
                    </LftTypography>
                  </S.TermosCondicoesTitulo>
                  <LftButton
                    size="sm"
                    actionLevel="tertiary"
                    ariaLabel={t('modalAvatar.termosCondicoes.politicaPrivacidade')}
                    lftClick={handleClickPrivatePolicy}
                    dataElement={converterParaKebabCase(t('modalAvatar.termosCondicoes.politicaPrivacidade'))}
                    icon={FileTextIcon}
                  >
                    {t('modalAvatar.termosCondicoes.politicaPrivacidade')}
                  </LftButton>
                  <LftButton
                    size="sm"
                    actionLevel="tertiary"
                    ariaLabel={t('modalAvatar.termosCondicoes.grupoEconomico')}
                    lftClick={handleClickEconomicGroup}
                    dataElement={converterParaKebabCase(t('modalAvatar.termosCondicoes.grupoEconomico'))}
                    icon={FileTextIcon}
                  >
                    {t('modalAvatar.termosCondicoes.grupoEconomico')}
                  </LftButton>
                </S.TermosCondicoesContent>
              )}
            </S.MenuMobileAvatarContent>
          }
          rodape={
            <S.RodapeWrapper>
              {(isLead || isEnabled('FF_EXIBE_BOTAO_SAIR')) && <Logout />}

              <MenuAcessibilidade dropdownPosition="top" />
            </S.RodapeWrapper>
          }
          onClose={toggleMenu}
          menuAberto={open}
        />
      )}
    </>
  );
};

export default MenuMobileAvatar;
